import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { DoorOpen, Calendar, Clock, CheckCircle, Phone, Mail, Lock, Smartphone, QrCode, Info } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { GUEST_TOKEN_DATA } from '@/data/mockData';

// ─── Countdown ────────────────────────────────────────────────────────────────

interface TimeLeft {
  days: number;
  hours: number;
  minutes: number;
  seconds: number;
  expired: boolean;
}

function useCountdown(endTs: string): TimeLeft {
  function calc(): TimeLeft {
    const diff = new Date(endTs).getTime() - Date.now();
    if (diff <= 0) return { days: 0, hours: 0, minutes: 0, seconds: 0, expired: true };
    const d = Math.floor(diff / 86400000);
    const h = Math.floor((diff % 86400000) / 3600000);
    const m = Math.floor((diff % 3600000) / 60000);
    const s = Math.floor((diff % 60000) / 1000);
    return { days: d, hours: h, minutes: m, seconds: s, expired: false };
  }

  const [timeLeft, setTimeLeft] = useState<TimeLeft>(calc);

  useEffect(() => {
    const timer = setInterval(() => setTimeLeft(calc()), 1000);
    return () => clearInterval(timer);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [endTs]);

  return timeLeft;
}

// ─── Countdown Block ──────────────────────────────────────────────────────────

function CountdownBlock({ endTs }: { endTs: string }) {
  const t = useCountdown(endTs);

  if (t.expired) {
    return (
      <div className="text-center py-4">
        <div className="text-red-500 font-semibold text-lg">Access window has closed.</div>
        <div className="text-sm text-white/60 mt-1">Please contact the property owner.</div>
      </div>
    );
  }

  const units = [
    { label: 'Days', value: t.days },
    { label: 'Hrs', value: t.hours },
    { label: 'Min', value: t.minutes },
    { label: 'Sec', value: t.seconds },
  ];

  return (
    <div className="flex gap-2 justify-center">
      {units.map(u => (
        <div key={u.label} className="text-center">
          <div
            className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-xl w-16 h-16 flex items-center justify-center text-2xl font-bold text-white"
            style={{ fontFamily: 'Montserrat, sans-serif' }}
          >
            {String(u.value).padStart(2, '0')}
          </div>
          <div className="text-xs text-white/60 mt-1 uppercase tracking-wider">{u.label}</div>
        </div>
      ))}
    </div>
  );
}

// ─── Enrol Dialog ─────────────────────────────────────────────────────────────

function EnrolDialog({ open, onClose, guestName }: { open: boolean; onClose: () => void; guestName: string }) {
  const [step, setStep] = useState(1);

  function next() { setStep(s => Math.min(s + 1, 3)); }

  return (
    <Dialog open={open} onOpenChange={() => { onClose(); setStep(1); }}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle style={{ fontFamily: 'Montserrat, sans-serif' }}>
            Enrol Your Mobile Credential
          </DialogTitle>
          <DialogDescription>
            Follow these steps to set up your digital key for Canopy Apartments.
          </DialogDescription>
        </DialogHeader>

        {/* Step indicators */}
        <div className="flex items-center gap-2 py-1">
          {[1, 2, 3].map(n => (
            <div key={n} className="flex items-center gap-2">
              <div className={`h-7 w-7 rounded-full flex items-center justify-center text-xs font-bold transition-all ${
                n < step ? 'bg-emerald-500 text-white' :
                n === step ? 'bg-primary text-white' :
                'bg-muted text-muted-foreground'
              }`}>
                {n < step ? <CheckCircle className="h-4 w-4" /> : n}
              </div>
              {n < 3 && <div className={`flex-1 h-0.5 w-8 ${n < step ? 'bg-emerald-500' : 'bg-border'}`} />}
            </div>
          ))}
        </div>

        {/* Steps */}
        <div className="py-2 min-h-36">
          {step === 1 && (
            <motion.div
              initial={{ opacity: 0, x: 10 }} animate={{ opacity: 1, x: 0 }}
              className="space-y-3"
            >
              <div className="flex items-start gap-3 bg-muted/60 rounded-xl p-4">
                <Smartphone className="h-5 w-5 text-accent mt-0.5 shrink-0" />
                <div>
                  <div className="font-semibold text-sm">Download the Inner Range Inception App</div>
                  <div className="text-sm text-muted-foreground mt-1">
                    Available on the App Store and Google Play. Search for <strong>"Inner Range Inception"</strong>.
                  </div>
                </div>
              </div>
              <div className="text-xs text-muted-foreground text-center">
                Already have the app? Skip to step 2.
              </div>
            </motion.div>
          )}

          {step === 2 && (
            <motion.div
              initial={{ opacity: 0, x: 10 }} animate={{ opacity: 1, x: 0 }}
              className="space-y-3"
            >
              <div className="flex items-start gap-3 bg-muted/60 rounded-xl p-4">
                <QrCode className="h-5 w-5 text-accent mt-0.5 shrink-0" />
                <div>
                  <div className="font-semibold text-sm">Scan Your Invitation</div>
                  <div className="text-sm text-muted-foreground mt-1">
                    Open the Inception app and tap <strong>Scan Invitation</strong>. Use the QR code
                    that was sent to <strong>{guestName}</strong>'s email, or tap the link in the app.
                  </div>
                </div>
              </div>
            </motion.div>
          )}

          {step === 3 && (
            <motion.div
              initial={{ opacity: 0, x: 10 }} animate={{ opacity: 1, x: 0 }}
              className="space-y-3"
            >
              <div className="flex items-start gap-3 bg-emerald-50 border border-emerald-200 rounded-xl p-4">
                <CheckCircle className="h-5 w-5 text-emerald-600 mt-0.5 shrink-0" />
                <div>
                  <div className="font-semibold text-sm text-emerald-700">You're all set!</div>
                  <div className="text-sm text-muted-foreground mt-1">
                    Your digital key is now active. Hold your phone near the reader at any of your
                    authorised entry points. Bluetooth must be enabled.
                  </div>
                </div>
              </div>
              <div className="bg-muted/60 rounded-xl p-3 text-xs text-muted-foreground flex gap-2">
                <Info className="h-4 w-4 shrink-0 mt-0.5" />
                Access is automatically revoked at your check-out time. No action needed from you.
              </div>
            </motion.div>
          )}
        </div>

        <div className="flex justify-between pt-2">
          <Button variant="outline" onClick={() => { onClose(); setStep(1); }}>
            {step === 3 ? 'Close' : 'Cancel'}
          </Button>
          {step < 3 && (
            <Button className="bg-primary text-primary-foreground hover:bg-primary/90" onClick={next}>
                {step === 1 ? "I've downloaded the app \u2192" : "I've scanned the code \u2192"}
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────

export default function GuestPortal() {
  const [showEnrol, setShowEnrol] = useState(false);
  const data = GUEST_TOKEN_DATA;

  function formatFull(ts: string): string {
    return new Date(ts).toLocaleString('en-AU', {
      weekday: 'long', day: 'numeric', month: 'long',
      year: 'numeric', hour: '2-digit', minute: '2-digit',
      timeZoneName: 'short', timeZone: 'Australia/Melbourne',
    });
  }

  function formatShort(ts: string): string {
    return new Date(ts).toLocaleDateString('en-AU', {
      day: 'numeric', month: 'short', year: 'numeric',
      hour: '2-digit', minute: '2-digit', timeZone: 'Australia/Melbourne',
    });
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Hero header */}
      <div
        className="relative overflow-hidden"
        style={{
          background: 'linear-gradient(135deg, #26324a 0%, color-mix(in srgb, #26324a 80%, black) 60%, #1a2235 100%)',
          minHeight: '420px',
        }}
      >
        {/* Decorative rings */}
        <div
          className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 rounded-full border border-white/5"
          style={{ width: 600, height: 600 }}
        />
        <div
          className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 rounded-full border border-white/5"
          style={{ width: 800, height: 800 }}
        />

        <div className="relative z-10 flex flex-col items-center justify-center text-center px-6 py-14">
          {/* Logo mark */}
          <motion.div
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ type: 'spring', stiffness: 300, damping: 25 }}
            className="h-16 w-16 rounded-2xl bg-accent flex items-center justify-center mb-6 shadow-lg"
            style={{ boxShadow: '0 8px 32px color-mix(in srgb, #458eae 40%, transparent)' }}
          >
            <Lock className="h-8 w-8 text-white" />
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
          >
            <p className="text-white/50 text-sm mb-1 tracking-widest uppercase">
              The Smarter Lock
            </p>
            <h1
              className="text-4xl font-bold text-white mb-2"
              style={{ fontFamily: 'Montserrat, sans-serif' }}
            >
              {data.buildingName}
            </h1>
            <p className="text-white/60 text-sm">{data.buildingAddress}</p>
          </motion.div>

          <motion.div
            className="mt-8 space-y-2"
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
          >
            <p className="text-white/70 text-sm">
              Welcome, <span className="text-white font-semibold">{data.guestName}</span>
              {' '}· Lot {data.lotNumber} (Host: {data.ownerName})
            </p>
            <p className="text-white/50 text-xs">Your access window:</p>
            <div className="flex items-center gap-2 justify-center text-white/80 text-sm">
              <Calendar className="h-4 w-4 text-accent" />
              <span>{formatShort(data.startTs)}</span>
              <span className="text-white/40">→</span>
              <span>{formatShort(data.endTs)}</span>
            </div>
          </motion.div>

          {/* Countdown */}
          <motion.div
            className="mt-8"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.35 }}
          >
            <p className="text-white/50 text-xs mb-3 uppercase tracking-wider">Time remaining</p>
            <CountdownBlock endTs={data.endTs} />
          </motion.div>
        </div>
      </div>

      {/* Content cards */}
      <div className="max-w-lg mx-auto px-4 -mt-6 space-y-4 pb-12">
        {/* Enrol CTA */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3, type: 'spring', stiffness: 300, damping: 28 }}
        >
          <div
            className="bg-accent rounded-2xl p-6 text-white text-center shadow-lg"
            style={{ boxShadow: '0 8px 32px color-mix(in srgb, #458eae 30%, transparent)' }}
          >
            <Smartphone className="h-8 w-8 mx-auto mb-3 opacity-90" />
            <h2 className="text-lg font-bold mb-1" style={{ fontFamily: 'Montserrat, sans-serif' }}>
              Set Up Your Digital Key
            </h2>
            <p className="text-white/80 text-sm mb-4">
              Enrol your mobile credential to unlock doors with your phone. Takes less than 2 minutes.
            </p>
            <Button
              className="w-full bg-white text-accent hover:bg-white/90 font-semibold"
              onClick={() => setShowEnrol(true)}
            >
              <Smartphone className="h-4 w-4 mr-2" />
              Enrol My Mobile Credential
            </Button>
          </div>
        </motion.div>

        {/* Door access */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4, type: 'spring', stiffness: 300, damping: 28 }}
          className="bg-card border border-border rounded-2xl p-5 shadow-sm"
        >
          <h3 className="font-semibold text-sm mb-3 flex items-center gap-2" style={{ fontFamily: 'Montserrat, sans-serif' }}>
            <DoorOpen className="h-4 w-4 text-accent" />
            Your Door Access
          </h3>
          <div className="space-y-2">
            {data.doors.map(door => (
              <div key={door} className="flex items-center justify-between text-sm py-1.5">
                <span className="text-foreground">{door}</span>
                <span className="text-xs bg-emerald-100 text-emerald-700 border border-emerald-200 px-2 py-0.5 rounded-full font-medium">
                  Authorised
                </span>
              </div>
            ))}
          </div>
          <div className="mt-3 pt-3 border-t border-border/50 flex items-center gap-2 text-xs text-muted-foreground">
            <Clock className="h-3.5 w-3.5" />
            Access expires automatically — no action needed.
          </div>
        </motion.div>

        {/* Access period detail */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.45 }}
          className="bg-card border border-border rounded-2xl p-5 shadow-sm"
        >
          <h3 className="font-semibold text-sm mb-3 flex items-center gap-2" style={{ fontFamily: 'Montserrat, sans-serif' }}>
            <Calendar className="h-4 w-4 text-accent" />
            Access Period
          </h3>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-muted-foreground">Check-in</span>
              <span className="font-medium text-right text-xs">{formatFull(data.startTs)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Check-out</span>
              <span className="font-medium text-right text-xs">{formatFull(data.endTs)}</span>
            </div>
          </div>
        </motion.div>

        {/* Support */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className="bg-muted/60 border border-border rounded-2xl p-5"
        >
          <h3 className="font-semibold text-sm mb-3 flex items-center gap-2 text-muted-foreground" style={{ fontFamily: 'Montserrat, sans-serif' }}>
            <Info className="h-4 w-4" />
            Need Help?
          </h3>
          <div className="space-y-2 text-sm text-muted-foreground">
            <div className="flex items-center gap-2">
              <Mail className="h-4 w-4 shrink-0" />
              <a href={`mailto:${data.supportEmail}`} className="hover:text-accent transition-colors">
                {data.supportEmail}
              </a>
            </div>
            <div className="flex items-center gap-2">
              <Phone className="h-4 w-4 shrink-0" />
              <a href={`tel:${data.supportPhone}`} className="hover:text-accent transition-colors">
                {data.supportPhone}
              </a>
            </div>
          </div>
          <div className="mt-3 pt-3 border-t border-border/50 text-xs text-muted-foreground">
            Access managed by The Smarter Lock — residential access governance for strata buildings.
          </div>
        </motion.div>
      </div>

      {/* Enrol dialog */}
      <EnrolDialog open={showEnrol} onClose={() => setShowEnrol(false)} guestName={data.guestName} />
    </div>
  );
}
